import React, { useMemo } from 'react';

const wrapProductName = (name = '', maxLineLength = 26, maxLines = 2) => {
  const upper = name ? String(name).toUpperCase() : '';
  const words = upper.split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [''];
  }
  if (maxLines === 1) {
    return [upper];
  }
  const lines = [];
  let current = '';
  words.forEach(word => {
    const tentative = current ? `${current} ${word}` : word;
    if (tentative.length <= maxLineLength) {
      current = tentative;
    } else {
      if (current) {
        lines.push(current);
      }
      current = word;
    }
  });
  if (current) {
    lines.push(current);
  }
  if (lines.length <= maxLines) {
    return lines;
  }
  const truncated = lines.slice(0, maxLines);
  const overflow = lines.slice(maxLines - 1).join(' ');
  truncated[maxLines - 1] = overflow.slice(0, maxLineLength).trim();
  return truncated;
};

const formatPriceParts = (price) => {
  const numeric = Number(price || 0);
  const [whole, cents] = numeric.toFixed(2).split('.');
  return { whole, cents };
};

const LabelPreview = React.forwardRef(({ name, barcode, price, unit = 'ea', currencySymbol = '$' }, ref) => {
  const lines = useMemo(() => wrapProductName(name, 26, 1), [name]);
  const { whole, cents } = useMemo(() => formatPriceParts(price), [price]);
  const barcodeValue = (barcode || '').trim();

  return (
    <div
      ref={ref}
      className="label-print-item"
      style={{
        margin: '6px',
        width: '260px',
        minHeight: '140px',
        borderRadius: '6px',
        border: '2px solid #d1d5db',
        background: '#fff',
        padding: '12px 14px 10px 12px',
        fontFamily: '\'Helvetica Neue\', Arial, sans-serif',
        color: '#111827',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        gap: '6px',
        textAlign: 'left'
      }}
    >
      {barcodeValue ? <div data-barcode-value={barcodeValue} className="text-left" style={{ display: 'none' }} /> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '100%', textAlign: 'left', paddingRight: '10px' }}>
        {lines.map((line, idx) => (
          <div
            key={`${line}-${idx}`}
            className="text-lg font-bold"
            style={{
              margin: 0,
              fontSize: '9px',
              fontWeight: 800,
              letterSpacing: '0.05em',
              lineHeight: 1.05
            }}
          >
            {line}
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '16px',
          marginTop: 'auto',
          width: '100%'
        }}
      >
        <div
          style={{
            flex: '1 1 55%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-end',
            gap: '4px',
            paddingBottom: '10px',
            minHeight: '110px'
          }}
        >
          {barcodeValue ? (
            <>
              <img
                src={`https://barcode.tec-it.com/barcode.ashx?data=${encodeURIComponent(barcodeValue)}&code=EAN13&dpi=120`}
                alt="barcode"
                style={{ height: '18px', objectFit: 'contain' }}
              />
              <div
                data-escpos-ignore="true"
                style={{ fontSize: '6px', letterSpacing: '0.06em', fontWeight: 700, paddingTop: '3px', textAlign: 'left', lineHeight: 1.1 }}
              >
                {barcodeValue}
              </div>
            </>
          ) : (
            <div style={{ fontSize: '16px', fontWeight: 600 }}>NO BARCODE</div>
          )}
        </div>

        <div
            className="text-right"
          style={{
            flex: '0 0 auto',
            textAlign: 'center',
            minWidth: '170px',
            maxWidth: '190px',
            position: 'relative',
            paddingBottom: '6px',
            paddingRight: '8px',
            marginRight: '10px',
            alignSelf: 'flex-end'
          }}
        >
          <div
            className="text-right text-xl font-bold"
            data-price-symbol={currencySymbol}
            data-price-major={whole}
            data-price-minor={cents}
            data-price-unit={unit}
            style={{ display: 'inline-flex', alignItems: 'flex-start', gap: '0px', lineHeight: 0.9, textAlign: 'center' }}
          >
            <span style={{ fontSize: '48px', fontWeight: 900 }}>{currencySymbol}</span>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0px' }}>
              <span style={{ fontSize: '140px', fontWeight: 900, lineHeight: 0.82 }}>{whole}</span>
              <span style={{ fontSize: '60px', fontWeight: 800, lineHeight: 1, position: 'relative', top: '-24px' }}>{cents}</span>
            </div>
          </div>
          <div
            data-escpos-ignore="true"
            style={{ fontSize: '16px', fontWeight: 700, letterSpacing: '0.06em', marginTop: '8px' }}
          >
            {unit}
          </div>
        </div>
      </div>
    </div>
  );
});

export default LabelPreview;
